<template>
  <!-- Drawer Overlay -->
  <div
    v-if="isOpen"
    class="fixed inset-0 font-inter bg-eerie-black bg-opacity-50 z-50 h-[100dvh]"
    @click="closeDrawer"
  >
    <!-- Drawer Content -->
    <div
      class="fixed top-0 text-lg right-0 w-64 border-l border-total-white-300 h-screen grid grid-rows-[1fr_2fr] bg-eerie-black text-total-white shadow-lg"
      @click.stop
    >
      <!-- Close Button -->
      <button class="absolute top-5 left-5" @click="closeDrawer">
        <i class="fas fa-times text-xl text-total-white"></i>
      </button>

      <div class="grid items-center">
        <!-- Contact Options -->
        <button
          @click="editContact"
          class="border-y grid grid-cols-[1fr_2fr] items-center p-4 border-total-white-300 text-prime-golden"
        >
          <i class="fas fa-pencil-alt text-prime-golden"></i>
          <span class="place-self-center">Edit Contact</span>
        </button>
      </div>

      <div class="mt-0 w-full text-prime-golden" v-if="!isSelf">
        <template v-if="contact.role === 'Project Agent'">
          <!-- For Project Agent, show only Text and Email buttons -->
          <button
            v-if="contact?.phone"
            @click="textContact"
            class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4 mt-20"
          >
            <img
              class="place-self-center"
              src="../assets/images/msg-icon.svg"
              alt="Text Icon"
            />
            <span class="place-self-center">Text {{ firstName }}</span>
          </button>
          <button
            v-if="contact?.email"
            @click="emailContact"
            class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4"
          >
            <i class="fas fa-envelope text-prime-golden"></i>
            <span class="place-self-center">Email {{ firstName }}</span>
          </button>
        </template>
        <template v-else>
          <!-- For any other role (except Project Manager) show all three buttons -->
          <button
            v-if="contact?.phone"
            @click="callContact"
            class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4 mt-20"
          >
            <i class="fas fa-phone text-prime-golden"></i>
            <span class="place-self-center">Call {{ firstName }}</span>
          </button>
          <button
            v-if="contact?.phone"
            @click="textContact"
            class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4"
          >
            <img
              class="place-self-center"
              src="../assets/images/msg-icon.svg"
              alt="Text Icon"
            />
            <span class="place-self-center">Text {{ firstName }}</span>
          </button>
          <button
            v-if="contact?.email"
            @click="emailContact"
            class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4"
          >
            <i class="fas fa-envelope text-prime-golden"></i>
            <span class="place-self-center">Email {{ firstName }}</span>
          </button>
        </template>

        <!-- Delete options -->
        <template v-if="contact.role !== 'Project Agent'">
          <template v-if="!projectId && !isUnknown">
            <button
              @click="deleteContactGlobally"
              class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4 mt-20"
            >
              <img src="../assets/images/trash-icon-red.svg" class="w-[16px] ml-6" />
              <span class="place-self-center text-sm text-red-500">Delete Contact</span>
            </button>
          </template>
          <template v-if="projectId && !isUnknown">
            <button
              @click="deleteContactFromProject"
              class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4 mt-20"
            >
              <img src="../assets/images/trash-icon-red.svg" class="w-[16px] ml-6" />
              <span class="place-self-center text-sm text-red-500"
                >Remove From Project</span
              >
            </button>
            <button
              @click="deleteContactGlobally"
              class="w-full grid items-center grid-cols-[1fr_2fr] border-y border-total-white-300 p-4"
            >
              <img src="../assets/images/trash-icon-red.svg" class="w-[16px] ml-6" />
              <span class="place-self-center text-sm text-red-500">Delete Contact</span>
            </button>

          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { useLogStore } from "@/stores/log";
import { useContactsStore } from "@/stores/contact";
import { useProjectStore } from "@/stores/project";
import { useToast } from "vue-toastification";

export default {
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    contact: {
      type: Object,
      required: true,
    },
    currentTab: {
      type: String,
      required: false,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  computed: {
    firstName() {
      return this.contact.first_name;
    },
    isSelf() {
      // If the contact's role is "Project Manager", then they are considered self.
      return this.contact.role === "Project Manager";
    },
    isUnknown() {
      const unknownNamesCase =
        this.contact.first_name === "Unknown" && this.contact.last_name === "Unknown";
      const emptyNamesCase = !this.contact.first_name && !this.contact.last_name;
      return unknownNamesCase || emptyNamesCase;
    },
  },
  methods: {
    closeDrawer() {
      this.$emit("close");
    },
    editContact() {
      this.$router.push({
        name: "ContactEditPage",
        params: {
          id: this.contact.id,
        },
        query: {
          projectId: this.projectId,
          tab: this.currentTab,
        },
      });
    },
    callContact() {
      this.$router.push({
        name: "NewCall",
        params: { projectId: this.projectId, contactId: this.contact.id },
        query: {
          contactId: this.contact.id,
          projectId: this.projectId,
          currentTab: this.currentTab,
        },
      });
    },
    async textContact() {
      const logStore = useLogStore();
      const contactsStore = useContactsStore();
      const projectStore = useProjectStore();

      const projectManagerTwilioNumber = projectStore.projects.find(
        (project) => project.id == this.projectId
      )?.project_manager_twilio_number?.phone_number;

      if (this.projectId) {
        await contactsStore.fetchContactsByProjectId(this.projectId);

        const projectManager = contactsStore.contacts.find(
          (contact) => contact.role === "Project Manager"
        );
        const threadId = await logStore.findTextThread(Number(this.projectId), [
          this.contact.phone,
          projectManager.phone,
        ]);
        if (threadId) {
          this.$router.push({
            name: "TextPage",
            params: {
              contactId: this.contact.id,
              projectId: this.projectId,
              threadId: threadId,
            },
            query: {
              contactId: this.contact.id,
              projectId: this.projectId,
              currentTab: this.currentTab,
            },
          });
        } else {
          this.$router.push({
            name: "NewTextMessage",
            params: { contactId: this.contact.id, projectId: this.projectId },
            query: {
              contactId: this.contact.id,
              projectId: this.projectId,
              currentTab: this.currentTab,
            },
          });
        }
      } else if (this.$route.name == "ContactBookPage") {
        this.$router.push({
          name: "NewTextMessage",
          params: { contactId: this.contact.id },
        });
      } else {
        this.$router.push({
          name: "NewTextMessage",
          params: { contactId: this.contact.id },
          query: { contactId: this.contact.id, from_contact: true },
        });
      }
    },
    emailContact() {
      if (this.projectId) {
        this.$router.push({
          name: "NewEmail",
          params: { contactId: this.contact.id, projectId: this.projectId },
          query: {
            contactId: this.contact.id,
            projectId: this.projectId,
            currentTab: this.currentTab,
          },
        });
      } else if (this.$route.name == "ContactBookPage") {
        this.$router.push({
          name: "NewEmailStandalone",
          params: { contactId: this.contact.id },
          query: { contactId: this.contact.id },
        });
      } else {
        this.$router.push({
          name: "NewEmailStandalone",
          params: { contactId: this.contact.id },
          query: { contactId: this.contact.id, from_contact: true },
        });
      }
    },
    async deleteContactGlobally() {
      const contactsStore = useContactsStore();
      const toast = useToast();
      try {
        await contactsStore.deleteContact(this.contact.id);
        this.$emit("close");
        this.$emit("refresh");
      } catch (error) {
        toast.error(error.message);
        console.error("Unable to delete contact:", error);
      }
    },
    async deleteContactFromProject() {
      const toast = useToast();
      try {
        const contactsStore = useContactsStore();
        await contactsStore.deleteContactFromProject(this.projectId, this.contact.id);
        this.$emit("close");
        this.$emit("refresh");
      } catch (error) {
        toast.error(error.message);
        console.error("Unable to delete contact:", error);
      }
    },
  },
};
</script>
